import React from 'react';
import './AmfiSlideContent.css';
import { RenderMarkdown } from '../chat/RenderMarkdown';
import { FaSync, FaSpinner } from 'react-icons/fa';
import useWindowSize from '../hooks/useWindowSize';

const safeguardValue = (value) => value || '';

const capitalizeWords = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const ResetButton = ({ slideId, contentKey, dimension, onResetDimension, isResettingThisSlide }) => {
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  if (!onResetDimension || !slideId) return null;
  return (
    <button
      onClick={() => onResetDimension(slideId)}
      title={`Reset this section (${contentKey || dimension})`}
      disabled={isResettingThisSlide}
      className="reset-section-button"
    >
      {isResettingThisSlide ? <FaSpinner className="spinner-small-button" /> : <FaSync />}
      {!isMobile && <span style={{ marginLeft: '8px' }}>{isResettingThisSlide ? 'Resetting...' : 'Reset Section'}</span>}
    </button>
  );
};

const DimensionHeader = ({ title, ...resetProps }) => (
  <div className="dimension-header">
    <h2>{title}</h2>
    <ResetButton {...resetProps} />
  </div>
);

// ── Objectives ────────────────────────────────────────────────
const ObjectivesContent = ({ learningObjectives = {}, ...resetProps }) => (
  <div className="slide amfi-objectives-content">
    <DimensionHeader title="Learning Objectives" {...resetProps} />
    {Object.entries(learningObjectives).map(([category, objectives]) => (
      <div key={category} className="content-section">
        <h3>{capitalizeWords(category)}</h3>
        <ul>
          {(Array.isArray(objectives) ? objectives : []).map((obj, i) => (
            <li key={i}><RenderMarkdown markdown={safeguardValue(obj)} /></li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

// ── Explore — Tafakkur × Analytical ──────────────────────────
const ExploreContent = ({ content = '', explanation = '', observations = '', fascinatingFacts = '', ...resetProps }) => (
  <div className="slide amfi-explore-content">
    <DimensionHeader title="Tafakkur × Explore" {...resetProps} />
    {content && (
      <div className="content-section">
        <h3>Content</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(content)} /></div>
      </div>
    )}
    {explanation && (
      <div className="content-section">
        <h3>Explanation</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(explanation)} /></div>
      </div>
    )}
    {observations && (
      <div className="content-section">
        <h3>Observations</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(observations)} /></div>
      </div>
    )}
    {fascinatingFacts && (
      <div className="content-section">
        <h3>Fascinating Facts</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(fascinatingFacts)} /></div>
      </div>
    )}
  </div>
);

// ── Compare — Tadabbur × Analogical ──────────────────────────
const CompareContent = ({ analogy = '', content = '', explanation = '', comparison = '', ...resetProps }) => (
  <div className="slide amfi-compare-content">
    <DimensionHeader title="Tadabbur × Compare" {...resetProps} />
    {analogy && (
      <div className="content-section">
        <h3>Analogy</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(analogy)} /></div>
      </div>
    )}
    {content && (
      <div className="content-section">
        <h3>Content</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(content)} /></div>
      </div>
    )}
    {explanation && (
      <div className="content-section">
        <h3>Explanation</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(explanation)} /></div>
      </div>
    )}
    {comparison && (
      <div className="content-section">
        <h3>Comparison</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(comparison)} /></div>
      </div>
    )}
  </div>
);

// ── Question — Iʿtibār × Critical ────────────────────────────
const QuestionContent = ({ questions = [], conclusion = '', subtopicIndex = 0, ...resetProps }) => {
  const titles = ['Negation of Chance', 'Negation of Material Causes', "Negation of 'Nature'", 'Conclusion'];
  const title = titles[subtopicIndex] || 'Question Section';
  return (
    <div className="slide amfi-question-content">
      <DimensionHeader title="Iʿtibār × Question" {...resetProps} />
      {subtopicIndex !== 3 && questions?.length > 0 && (
        <div className="content-section">
          <h3>{title}</h3>
          <ul>
            {questions.map((q, i) => <li key={i}><RenderMarkdown markdown={safeguardValue(q)} /></li>)}
          </ul>
        </div>
      )}
      {subtopicIndex === 3 && conclusion && (
        <div className="content-section">
          <h3>Conclusion</h3>
          <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(conclusion)} /></div>
        </div>
      )}
    </div>
  );
};

// ── Connect — Tadhakkur × Meditative ─────────────────────────
const ConnectContent = ({
  contentKey = '', interconnectedness = '', interdependency = '', completeDependency = '',
  allahNames = {}, subtopicIndex = 0, ...resetProps
}) => {
  const names = {
    namesInEnglish: Array.isArray(allahNames?.namesInEnglish) ? allahNames.namesInEnglish : Array.isArray(allahNames?.names_in_english) ? allahNames.names_in_english : [],
    namesInArabic:  Array.isArray(allahNames?.namesInArabic)  ? allahNames.namesInArabic  : Array.isArray(allahNames?.names_in_arabic)  ? allahNames.names_in_arabic  : [],
    whatItTells:    Array.isArray(allahNames?.whatItTells)     ? allahNames.whatItTells     : Array.isArray(allahNames?.what_it_tells)     ? allahNames.what_it_tells    : [],
  };
  const interdepValue = interdependency || completeDependency;
  const keyTitles = { interconnectedness: 'Interconnectedness', interdependency: 'Interdependency', completeDependency: 'Complete Dependency', allahNames: "Allah's Names" };
  const sectionKey = contentKey || ['interconnectedness', 'interdependency', 'allahNames'][subtopicIndex] || 'interconnectedness';
  const sectionTitle = keyTitles[sectionKey] || sectionKey;
  const rowCount = Math.max(names.namesInEnglish.length, names.namesInArabic.length, names.whatItTells.length);
  const rows = Array.from({ length: rowCount }, (_, i) => ({ namesInEnglish: names.namesInEnglish[i] || '', namesInArabic: names.namesInArabic[i] || '', whatItTells: names.whatItTells[i] || '' }));

  return (
    <div className="slide amfi-connect-content">
      <DimensionHeader title="Tadhakkur × Connect" {...resetProps} />
      {sectionKey === 'interconnectedness' && interconnectedness && (
        <div className="content-section">
          <h3>{sectionTitle}</h3>
          <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(interconnectedness)} /></div>
        </div>
      )}
      {(sectionKey === 'interdependency' || sectionKey === 'completeDependency') && interdepValue && (
        <div className="content-section">
          <h3>{sectionTitle}</h3>
          <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(interdepValue)} /></div>
        </div>
      )}
      {sectionKey === 'allahNames' && rows.length > 0 && (
        <div className="content-section">
          <h3>{sectionTitle}</h3>
          <table>
            <thead>
              <tr><th>What it tells us</th><th>Name in English</th><th>Name in Arabic</th></tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td><RenderMarkdown markdown={safeguardValue(row.whatItTells)} /></td>
                  <td><RenderMarkdown markdown={safeguardValue(row.namesInEnglish)} /></td>
                  <td style={{ fontFamily: 'Amiri, Cairo, serif', fontSize: '1.2em', direction: 'rtl', textAlign: 'right' }}>{row.namesInArabic}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Appreciate — Khilāfah × Moral ────────────────────────────
const AppreciateContent = ({
  whatIfs = '', zikrFikrShukr = {}, characterLessons = '',
  connectWithQuran = [], connectWithHadith = [], dua = '', ...resetProps
}) => (
  <div className="slide amfi-appreciate-content">
    <DimensionHeader title="Khilāfah × Appreciate" {...resetProps} />
    {whatIfs && (
      <div className="content-section">
        <h3>What Ifs</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(whatIfs)} /></div>
      </div>
    )}
    {zikrFikrShukr?.zikr?.length > 0 && zikrFikrShukr?.fikr?.length > 0 && zikrFikrShukr?.shukr?.length > 0 && (
      <div className="content-section">
        <h3>Dhikr · Fikr · Shukr</h3>
        <table>
          <thead>
            <tr><th>Dhikr — Remember</th><th>Fikr — Reflect</th><th>Shukr — Thank</th></tr>
          </thead>
          <tbody>
            {(zikrFikrShukr.zikr || []).map((item, i) => (
              <tr key={i}>
                <td><RenderMarkdown markdown={safeguardValue(item)} /></td>
                <td><RenderMarkdown markdown={safeguardValue(zikrFikrShukr.fikr?.[i])} /></td>
                <td><RenderMarkdown markdown={safeguardValue(zikrFikrShukr.shukr?.[i])} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {characterLessons && (
      <div className="content-section">
        <h3>Character Lessons</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(characterLessons)} /></div>
      </div>
    )}
    {connectWithQuran?.length > 0 && (
      <div className="content-section">
        <h3>Connect with Quran</h3>
        {connectWithQuran.map((q, i) => (
          <div key={i} className="quran-item">
            <p><strong>Verse:</strong> <RenderMarkdown markdown={safeguardValue(q.verse)} /></p>
            <p><strong>Translation:</strong> <RenderMarkdown markdown={safeguardValue(q.translation)} /></p>
            {q.explanation && <p><strong>Explanation:</strong> <RenderMarkdown markdown={safeguardValue(q.explanation)} /></p>}
          </div>
        ))}
      </div>
    )}
    {connectWithHadith?.length > 0 && (
      <div className="content-section">
        <h3>Connect with Hadith</h3>
        {connectWithHadith.map((h, i) => (
          <div key={i} className="hadith-item">
            <p><strong>Hadith:</strong> <RenderMarkdown markdown={safeguardValue(h.hadith)} /></p>
            {h.explanation && <p><strong>Explanation:</strong> <RenderMarkdown markdown={safeguardValue(h.explanation)} /></p>}
          </div>
        ))}
      </div>
    )}
    {dua && (
      <div className="content-section">
        <h3>Dua</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(dua)} /></div>
      </div>
    )}
  </div>
);

// ── Activities ────────────────────────────────────────────────
const ActivitiesContent = ({ activities = [], ...resetProps }) => (
  <div className="slide amfi-activities-content">
    <DimensionHeader title="Activities" {...resetProps} />
    {activities.map((activity, i) => (
      <div key={i} className="activity-section">
        <h3>{activity.title || 'Activity'}</h3>
        <p><strong>{activity.stage ? 'Stage' : 'Dimension'}:</strong> {activity.stage || activity.dimension || ''}</p>
        <p><strong>Objective:</strong> <RenderMarkdown markdown={safeguardValue(activity.objective)} /></p>
        <div className="content-section">
          <h4>Materials</h4>
          <ul>{(activity.materials || []).map((m, j) => <li key={j}><RenderMarkdown markdown={safeguardValue(m)} /></li>)}</ul>
        </div>
        <div className="content-section">
          <h4>Instructions</h4>
          <ol>{(activity.instructions || []).map((ins, j) => <li key={j}><RenderMarkdown markdown={safeguardValue(ins)} /></li>)}</ol>
        </div>
      </div>
    ))}
  </div>
);

// ══════════════════════════════════════════════════════════════
// PURE AMFI DIMENSION COMPONENTS
// ══════════════════════════════════════════════════════════════

// ── Tafakkur — Think ─────────────────────────────────────────
const TafakkurContent = ({
  contentKey = '', content = '', observations = '', fascinatingFacts = '',
  muslimScholar = null, creationVsDesign = null, ...resetProps
}) => (
  <div className="slide amfi-explore-content">
    <DimensionHeader title="Tafakkur — Think" {...resetProps} />
    {contentKey === 'content' && content && (
      <div className="content-section">
        <h3>What Is Really Going On Here?</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(content)} /></div>
      </div>
    )}
    {contentKey === 'observations' && observations && (
      <div className="content-section">
        <h3>Observations</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(observations)} /></div>
      </div>
    )}
    {contentKey === 'fascinatingFacts' && Array.isArray(fascinatingFacts) && fascinatingFacts.length > 0 && (
      <div className="content-section">
        <h3>Did You Know?</h3>
        <ul>{fascinatingFacts.map((f, i) => <li key={i}><RenderMarkdown markdown={safeguardValue(f)} /></li>)}</ul>
      </div>
    )}
    {contentKey === 'muslimScholar' && muslimScholar && (
      <div className="content-section">
        <h3>Muslim Scholar Spotlight</h3>
        <p><strong>{muslimScholar.name}</strong></p>
        {muslimScholar.contribution && <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(muslimScholar.contribution)} /></div>}
        {muslimScholar.islamicConnection && (
          <div className="quran-item" style={{ marginTop: '0.75rem' }}>
            <p><strong>Islamic Connection:</strong></p>
            <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(muslimScholar.islamicConnection)} /></div>
          </div>
        )}
      </div>
    )}
    {contentKey === 'creationVsDesign' && creationVsDesign && (
      <div className="content-section">
        <h3>Creation vs. Human Design</h3>
        {creationVsDesign.gapStatement && (
          <div className="markdown-content" style={{ marginBottom: '1rem' }}>
            <RenderMarkdown markdown={safeguardValue(creationVsDesign.gapStatement)} />
          </div>
        )}
        {Array.isArray(creationVsDesign.comparisonPairs) && creationVsDesign.comparisonPairs.length > 0 && (
          <table>
            <thead>
              <tr><th>Human Design — {creationVsDesign.humanDesign}</th><th>Divine Creation — {creationVsDesign.divineCreation}</th></tr>
            </thead>
            <tbody>
              {creationVsDesign.comparisonPairs.map((pair, i) => (
                <tr key={i}>
                  <td><RenderMarkdown markdown={safeguardValue(pair.human)} /></td>
                  <td><RenderMarkdown markdown={safeguardValue(pair.divine)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    )}
  </div>
);

// ── Tadabbur — Reflect ────────────────────────────────────────
const TadabburContent = ({
  contentKey = '', content = '', reflectionQuestions = [], designWisdom = '', ...resetProps
}) => (
  <div className="slide amfi-compare-content">
    <DimensionHeader title="Tadabbur — Reflect" {...resetProps} />
    {contentKey === 'content' && content && (
      <div className="content-section">
        <h3>What If This Didn't Exist?</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(content)} /></div>
      </div>
    )}
    {contentKey === 'reflectionQuestions' && Array.isArray(reflectionQuestions) && reflectionQuestions.length > 0 && (
      <div className="content-section">
        <h3>Reflection Questions</h3>
        <ul>{reflectionQuestions.map((q, i) => <li key={i}><RenderMarkdown markdown={safeguardValue(q)} /></li>)}</ul>
      </div>
    )}
    {contentKey === 'designWisdom' && designWisdom && (
      <div className="content-section">
        <h3>The Wisdom in the Design</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(designWisdom)} /></div>
      </div>
    )}
  </div>
);

// ── Tadhakkur — Remember ──────────────────────────────────────
const TadhakkurContent = ({
  contentKey = '', interconnectedness = '', continuousCreation = '',
  allahNames = {}, revelationAnchor = null, ...resetProps
}) => {
  const names = {
    namesInEnglish: Array.isArray(allahNames?.namesInEnglish) ? allahNames.namesInEnglish : [],
    namesInArabic:  Array.isArray(allahNames?.namesInArabic)  ? allahNames.namesInArabic  : [],
    whatItTells:    Array.isArray(allahNames?.whatItTells)     ? allahNames.whatItTells     : [],
  };
  const rowCount = Math.max(names.namesInEnglish.length, names.namesInArabic.length, names.whatItTells.length);
  const rows = Array.from({ length: rowCount }, (_, i) => ({
    namesInEnglish: names.namesInEnglish[i] || '',
    namesInArabic:  names.namesInArabic[i]  || '',
    whatItTells:    names.whatItTells[i]    || '',
  }));

  return (
    <div className="slide amfi-connect-content">
      <DimensionHeader title="Tadhakkur — Remember" {...resetProps} />
      {contentKey === 'interconnectedness' && interconnectedness && (
        <div className="content-section">
          <h3>Interconnectedness</h3>
          <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(interconnectedness)} /></div>
        </div>
      )}
      {contentKey === 'continuousCreation' && continuousCreation && (
        <div className="content-section">
          <h3>Continuous Creation</h3>
          <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(continuousCreation)} /></div>
        </div>
      )}
      {contentKey === 'allahNames' && rows.length > 0 && (
        <div className="content-section">
          <h3>The Names of Allah</h3>
          <table>
            <thead>
              <tr><th>What It Tells Us</th><th>Name in English</th><th>Name in Arabic</th></tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td><RenderMarkdown markdown={safeguardValue(row.whatItTells)} /></td>
                  <td><RenderMarkdown markdown={safeguardValue(row.namesInEnglish)} /></td>
                  <td style={{ fontFamily: 'Amiri, Cairo, serif', fontSize: '1.2em', direction: 'rtl', textAlign: 'right' }}>{row.namesInArabic}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {contentKey === 'revelationAnchor' && revelationAnchor && (
        <div className="content-section">
          <h3>Revelation Anchor</h3>
          <div className="quran-item">
            {revelationAnchor.verse && (
              <p style={{ fontFamily: 'Amiri, Cairo, serif', fontSize: '1.15em', direction: 'rtl', textAlign: 'right', lineHeight: 2.2, color: '#0D4F5C' }}>{revelationAnchor.verse}</p>
            )}
            {revelationAnchor.reference && <p><strong>{revelationAnchor.reference}</strong></p>}
            {revelationAnchor.translation && <p><em>{revelationAnchor.translation}</em></p>}
            {revelationAnchor.explanation && <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(revelationAnchor.explanation)} /></div>}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Iʿtibār — Learn ──────────────────────────────────────────
const ItibarContent = ({
  contentKey = '', questions = [], conclusion = '', historicalLesson = '', moralLesson = '',
  subtopicIndex = 0, ...resetProps
}) => {
  const titleMap = {
    negation_of_chance: 'Could This Have Happened by Chance?',
    negation_of_material_causes: 'Could the Materials Have Done This Alone?',
    negation_of_autonomous_processes: "Can 'Natural Processes' Explain This?",
    conclusion: 'What We Conclude',
    historicalLesson: 'Historical Lesson',
    moralLesson: 'Moral Lesson',
  };
  const title = titleMap[contentKey] || 'Iʿtibār — Learn';

  return (
    <div className="slide amfi-question-content">
      <DimensionHeader title="Iʿtibār — Learn" {...resetProps} />
      {['negation_of_chance', 'negation_of_material_causes', 'negation_of_autonomous_processes'].includes(contentKey) && Array.isArray(questions) && questions.length > 0 && (
        <div className="content-section">
          <h3>{title}</h3>
          <ul>{questions.map((q, i) => <li key={i}><RenderMarkdown markdown={safeguardValue(q)} /></li>)}</ul>
        </div>
      )}
      {contentKey === 'conclusion' && conclusion && (
        <div className="content-section">
          <h3>{title}</h3>
          <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(conclusion)} /></div>
        </div>
      )}
      {contentKey === 'historicalLesson' && historicalLesson && (
        <div className="content-section">
          <h3>{title}</h3>
          <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(historicalLesson)} /></div>
        </div>
      )}
      {contentKey === 'moralLesson' && moralLesson && (
        <div className="content-section">
          <h3>{title}</h3>
          <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(moralLesson)} /></div>
        </div>
      )}
    </div>
  );
};

// ── Khilāfah — Act ────────────────────────────────────────────
const KhilafahContent = ({
  contentKey = '', whatIfs = '', zikrFikrShukr = {}, characterLessons = null,
  stewardshipActions = [], connectWithQuran = [], connectWithHadith = [], dua = '', ...resetProps
}) => (
  <div className="slide amfi-appreciate-content">
    <DimensionHeader title="Khilāfah — Act" {...resetProps} />
    {contentKey === 'whatIfs' && whatIfs && (
      <div className="content-section">
        <h3>What If This Didn't Exist?</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(whatIfs)} /></div>
      </div>
    )}
    {contentKey === 'zikrFikrShukr' && zikrFikrShukr?.zikr?.length > 0 && (
      <div className="content-section">
        <h3>Dhikr · Fikr · Shukr</h3>
        <table>
          <thead>
            <tr><th>Dhikr — Remember</th><th>Fikr — Reflect</th><th>Shukr — Thank</th></tr>
          </thead>
          <tbody>
            {(zikrFikrShukr.zikr || []).map((item, i) => (
              <tr key={i}>
                <td><RenderMarkdown markdown={safeguardValue(item)} /></td>
                <td><RenderMarkdown markdown={safeguardValue(zikrFikrShukr.fikr?.[i])} /></td>
                <td><RenderMarkdown markdown={safeguardValue(zikrFikrShukr.shukr?.[i])} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {contentKey === 'characterLessons' && characterLessons && (
      <div className="content-section">
        <h3>Character Lessons</h3>
        {typeof characterLessons === 'object' && !Array.isArray(characterLessons) ? (
          <>
            {characterLessons.professional && <div className="content-section"><h4>Professional</h4><div className="markdown-content"><RenderMarkdown markdown={safeguardValue(characterLessons.professional)} /></div></div>}
            {characterLessons.moral && <div className="content-section"><h4>Moral</h4><div className="markdown-content"><RenderMarkdown markdown={safeguardValue(characterLessons.moral)} /></div></div>}
            {characterLessons.spiritual && <div className="content-section"><h4>Spiritual</h4><div className="markdown-content"><RenderMarkdown markdown={safeguardValue(characterLessons.spiritual)} /></div></div>}
          </>
        ) : (
          <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(String(characterLessons))} /></div>
        )}
      </div>
    )}
    {contentKey === 'stewardshipActions' && Array.isArray(stewardshipActions) && stewardshipActions.length > 0 && (
      <div className="content-section">
        <h3>Your Amānah — Your Trust</h3>
        <ul>{stewardshipActions.map((a, i) => <li key={i}><RenderMarkdown markdown={safeguardValue(a)} /></li>)}</ul>
      </div>
    )}
    {contentKey === 'connectWithQuran' && connectWithQuran?.length > 0 && (
      <div className="content-section">
        <h3>Connect with Quran</h3>
        {connectWithQuran.map((q, i) => (
          <div key={i} className="quran-item">
            {q.verse && <p style={{ fontFamily: 'Amiri, Cairo, serif', fontSize: '1.15em', direction: 'rtl', textAlign: 'right', lineHeight: 2.2, color: '#0D4F5C' }}>{q.verse}</p>}
            {q.reference && <p><strong>{q.reference}</strong></p>}
            <p><em>{q.translation}</em></p>
            {q.explanation && <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(q.explanation)} /></div>}
          </div>
        ))}
      </div>
    )}
    {contentKey === 'connectWithHadith' && connectWithHadith?.length > 0 && (
      <div className="content-section">
        <h3>Connect with Hadith</h3>
        {connectWithHadith.map((h, i) => (
          <div key={i} className="hadith-item">
            <p><strong>Hadith:</strong> <RenderMarkdown markdown={safeguardValue(h.hadith)} /></p>
            {h.explanation && <p><RenderMarkdown markdown={safeguardValue(h.explanation)} /></p>}
          </div>
        ))}
      </div>
    )}
    {contentKey === 'dua' && dua && (
      <div className="content-section">
        <h3>Dua</h3>
        <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(dua)} /></div>
      </div>
    )}
  </div>
);

// ── Main router ───────────────────────────────────────────────
const AmfiSlideContent = ({ slide, onResetDimension, isResettingThisSlide }) => {
  const resetProps = {
    slideId: slide.id,
    contentKey: slide.contentKey,
    dimension: slide.dimension,
    onResetDimension,
    isResettingThisSlide,
  };
  const render = (Component) => <Component {...slide} {...resetProps} />;

  return (
    <div className="amfi-slide-container">
      {(() => {
        switch (slide.dimension) {
          // AMFI × 5DT dimensions
          case 'Objectives':  return render(ObjectivesContent);
          case 'Explore':     return render(ExploreContent);
          case 'Compare':     return render(CompareContent);
          case 'Question':    return render(QuestionContent);
          case 'Connect':     return render(ConnectContent);
          case 'Appreciate':  return render(AppreciateContent);
          case 'Activities':  return render(ActivitiesContent);
          // Pure AMFI dimensions
          case 'Tafakkur':    return render(TafakkurContent);
          case 'Tadabbur':    return render(TadabburContent);
          case 'Tadhakkur':   return render(TadhakkurContent);
          case 'Iʿtibār':    return render(ItibarContent);
          case 'Khilāfah':   return render(KhilafahContent);
          default:            return null;
        }
      })()}
    </div>
  );
};

export default AmfiSlideContent;
